import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, RotateCcw, Home } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false
    };
  }

  public static getDerivedStateFromError(error: Error): State {

    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {

    
    // Você poderia enviar isso para um serviço de monitoramento de erros como Sentry
    this.setState({ errorInfo });
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleGoHome = (): void => {
    this.handleReset();
    window.location.href = '/';
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      // Se um fallback customizado for fornecido, renderize-o
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
          <Card className="w-full max-w-md shadow-xl border-destructive/20">
            <CardHeader className="text-center border-b pb-4">
              <div className="mx-auto bg-destructive/10 text-destructive p-3 rounded-full mb-2">
                <AlertCircle size={24} />
              </div>
              <CardTitle className="text-2xl font-bold text-destructive">Ocorreu um erro</CardTitle>
              <CardDescription>
                Algo inesperado aconteceu na aplicação
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <p className="text-muted-foreground text-center">
                Pedimos desculpas pelo inconveniente. Você pode tentar recarregar a página ou voltar para a página inicial.
              </p>
              
              <div className="flex space-x-2 justify-center">
                <Button 
                  onClick={(): void => window.location.reload()}
                  variant="outline"
                  className="gap-2"
                >
                  <RotateCcw size={16} />
                  Recarregar página
                </Button>
                <Button 
                  onClick={this.handleGoHome}
                  className="gap-2"
                >
                  <Home size={16} />
                  Página inicial
                </Button>
              </div>
              
              {this.state.error && (
                <details className="mt-6 text-left text-sm">
                  <summary className="cursor-pointer text-primary">Detalhes técnicos do erro</summary>
                  <div className="mt-2 p-3 bg-muted rounded-md space-y-2 text-xs">
                    <div>
                      <strong className="font-semibold">Mensagem:</strong>
                      <p className="mt-1 text-destructive font-mono">{this.state.error.message}</p>
                    </div>
                    {this.state.error.stack && (
                      <div>
                        <strong className="font-semibold">Stack Trace:</strong>
                        <pre className="mt-1 overflow-auto p-2 bg-muted/50 rounded-md font-mono text-destructive/80">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                    {this.state.errorInfo && (
                      <div>
                        <strong className="font-semibold">Componente:</strong>
                        <pre className="mt-1 overflow-auto p-2 bg-muted/50 rounded-md font-mono text-destructive/80">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}